import {
  BriefcaseBusiness,
  CalendarClock,
  GraduationCap,
  Lightbulb,
  Network,
  Search,
  Share2,
  Target,
  Users,
  Wrench,
} from 'lucide-react';
import AboutHeader from '../../components/about/AboutHeader';
import BenefitSection from '../../components/about/BenefitSection';
import FeatureCard from '../../components/about/FeatureCard';
import UseCaseCard from '../../components/about/UseCaseCard';
import { useTheme } from '../../context/ThemeContext';

const features = [
  {
    icon: Search,
    title: 'Find People',
    description: 'Search employees by skills,  experience, location, availability, certifications, and expertise.',
  },
  {
    icon: Users,
    title: 'Lists & Collaboration',
    description: 'Create project groups, customer call groups, and problem-solving groups for team collaboration.',
  },
  {
    icon: Target,
    title: 'Skill Discovery',
    description: 'Identify internal experts before hiring external resources.',
  },
  {
    icon: CalendarClock,
    title: 'Availability Management',
    description: 'View employee availability and available hours for projects, mentoring, and support.',
  },
  {
    icon: Network,
    title: 'Community Building',
    description: 'Build professional and ExploreTechnologies-based communities across the organization.',
  },
  {
    icon: GraduationCap,
    title: 'Knowledge Sharing',
    description: 'Encourage mentoring, collaboration, and organizational learning.',
  },
];

const employeeBenefits = [
  'Showcase skills and expertise',
  'Discover internal opportunities',
  'Join project teams',
  'Participate in customer discussions',
  'Connect with mentors',
  'Build professional networks',
  'Join communities of ExploreTechnologies',
  'Increase visibility across the organization',
];

const managerBenefits = [
  'Quickly identify experts',
  'Find resources for projects',
  'Locate specialists for customer calls',
  'Discover available talent',
  'Reduce project staffing effort',
  'Improve utilization of internal expertise',
  'Build high-performing teams faster',
];

const organizationBenefits = [
  'Better employee engagement',
  'Faster collaboration',
  'Increased knowledge sharing',
  'Reduced dependency on external hiring',
  'Improved project delivery',
  'Stronger internal networking',
  'Better resource utilization',
  'Enhanced organizational culture',
];

const useCases = [
  {
    icon: Wrench,
    title: 'Problem Solving',
    description: 'Find employees who can help resolve technical or business challenges.',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Projects',
    description: 'Identify available experts for short-term or long-term initiatives.',
  },
  {
    icon: Share2,
    title: 'Customer Calls',
    description: 'Bring the right specialists into prospect discussions and customer meetings.',
  },
  {
    icon: Lightbulb,
    title: 'Mentoring',
    description: 'Connect experienced employees with learners.',
  },
];

const roadmapItems = [
  {
    title: 'AI-Powered Organizational Intelligence',
    description:
      'AI-powered insights to help organizations better understand workforce engagement, emerging skills, collaboration trends, and growth opportunities.',
  },
  {
    title: 'Public-Facing Platform',
    description:
      'Expansion of ISentryx into a public-facing professional networking ecosystem that enables professionals, communities, and organizations to connect, collaborate, and share expertise beyond enterprise boundaries.',
  },
];

export default function AboutPage() {
  const { theme } = useTheme();

  return (
    <div className="space-y-6">
      <AboutHeader />

      <section className="rounded-lg border bg-white p-6 shadow-sm" style={{ borderColor: theme.border, backgroundColor: theme.surface }}>
        <h2 className="mb-3 text-xl font-bold" style={{ color: theme.text }}>
          What Is ISentryx
        </h2>
        <div className="space-y-3 text-sm leading-7 sm:text-base" style={{ color: theme.textSecondary }}>
          <p className="mb-0">
            ISentryx is an internal enterprise networking platform designed to help organizations discover talent,
            connect employees, build communities, and accelerate collaboration.
          </p>
          <p className="mb-0">
            The platform enables employees to showcase professional skills, expertise, and availability
            while helping managers/employees quickly identify the right people for projects, problem-solving, mentoring, and
            customer engagements.
          </p>
        </div>
      </section>

      <section className="rounded-lg border bg-white p-6 shadow-sm" style={{ borderColor: theme.border, backgroundColor: theme.surface }}>
        <h2 className="mb-3 text-xl font-bold" style={{ color: theme.text }}>
          Future Road Map
        </h2>

        <div className="space-y-3 text-sm leading-7 sm:text-base" style={{ color: theme.textSecondary }}>
          <p className="mb-0">
            The long-term vision for ISentryx extends beyond employee networking into enterprise intelligence
            and broader professional collaboration.
          </p>

          {roadmapItems.map((item) => (
            <div key={item.title} className="space-y-3">
              <h3 className="mb-0 text-sm font-bold sm:text-base" style={{ color: theme.text }}>
                {item.title}
              </h3>
              <p className="mb-0">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-4 text-xl font-bold" style={{ color: theme.text }}>
          Key Features
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {features.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <BenefitSection title="Benefits For Employees" benefits={employeeBenefits} />
        <BenefitSection title="Benefits For Managers" benefits={managerBenefits} />
        <BenefitSection title="Benefits For Organizations" benefits={organizationBenefits} />
      </div>

      <section>
        <h2 className="mb-4 text-xl font-bold" style={{ color: theme.text }}>
          Use Cases
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {useCases.map((useCase) => (
            <UseCaseCard key={useCase.title} {...useCase} />
          ))}
        </div>
      </section>

      <section className="rounded-lg border bg-white p-6 shadow-sm" style={{ borderColor: theme.border, backgroundColor: theme.surface }}>
        <h2 className="mb-3 text-xl font-bold" style={{ color: theme.text }}>
          Building a Connected Workforce
        </h2>
        <p className="mb-0 max-w-4xl text-sm leading-7 sm:text-base" style={{ color: theme.textSecondary }}>
          ISentryx helps organizations unlock the full potential of their workforce by connecting people, skills,
          knowledge, and opportunities in a single platform.
        </p>
      </section>
    </div>
  );
}
